import { ref, query, orderByChild, startAt, endAt, limitToFirst, get, update, runTransaction } from 'firebase/database';
import { removeSlotLocksWhere } from './bookingAvailabilityUtil';
import {
  AUDIT_ACTION_TYPES,
  CLEANUP_AUDIT_MARGIN_MS,
  CLEANUP_DELETE_CHUNK_SIZE,
  CLEANUP_GROUPS,
} from '../constants/bookingConst';

// ---------------------------------------------------------------------------
// Dirty-booking cleanup (Admin -> Booking Management -> Cleanup Booking).
//
// THE TEST, AND WHY IT WORKS
// Every booking that went through a screen — a guest checkout, or the admin's
// own "Book New Slot" — writes a `booking_submitted` entry to /auditLog at the
// moment it becomes reviewable. A record written straight to the database over
// the REST API never does, because nothing but the app's own code path writes
// that entry. So "a booking record with no booking_submitted entry" is the
// closest thing to a signature that script-injected records have.
//
// THE ONE FALSE POSITIVE THIS MUST NOT SWALLOW
// A booking sits at `awaiting_payment` from the moment its slots are claimed
// until the guest uploads a receipt, and the audit entry is only written at
// that upload (see App.jsx's handleReceiptUploaded). An abandoned checkout is
// therefore a genuine record with no submit entry, through no fault of anyone.
// Those are classified separately — never mixed in with the dirty ones — and
// the screen leaves them unticked.
//
// A SECOND ONE, WHICH THE SCREEN LABELS RATHER THAN HIDES
// Bookings created before audit logging shipped have no entry either. Nothing
// in the data distinguishes those from injected records, so this module
// reports the oldest audit entry's timestamp and lets the screen warn when the
// selected range reaches back past it. Deliberately a warning, not a block —
// a club whose junk predates its audit log still needs to clear it.
// ---------------------------------------------------------------------------

/**
 * Every entityId with a `booking_submitted` entry in (and around) a time
 * window, as one query rather than one lookup per booking — 4,000 bookings
 * would otherwise be 4,000 round trips.
 *
 * ORDERED BY TIMESTAMP, FILTERED IN JS. `actionType` is indexed, and ordering
 * by it would be the obvious query, but /auditLog's read rule is
 *
 *   "auth != null && (query.orderByChild == 'entityId' || query.orderByChild == 'timestamp')"
 *
 * — deliberately, so no client can pull the log with an unbounded request.
 * An orderByChild('actionType') query is refused with permission_denied no
 * matter what the index says, so the ordering has to be one of those two and
 * the action type is matched here instead.
 *
 * THE MARGIN. A booking's submit entry is written when the guest uploads a
 * receipt, which is minutes after the record itself is created — but "minutes
 * after" can fall the other side of a range boundary, and an admin-made
 * booking logs instantly. So the window is widened by CLEANUP_AUDIT_MARGIN_MS
 * at both ends: a submit entry that lands outside the queried window would
 * make its perfectly genuine booking read as dirty, and the cost of the
 * margin is only a few more small records.
 *
 * @param {object} db - Firebase Realtime Database instance
 * @param {number} startMs - lower bound of the range being cleaned
 * @param {number} endMs - upper bound
 * @returns {Promise<{ids: Set<string>, windowStartMs: number, windowEndMs: number, scanned: number}>}
 *   `ids` holds entityIds, which for a multi-date booking is the group id
 *   (see logAuditEvent's entityId parameter)
 */
export const fetchSubmittedEntityIds = async (db, startMs, endMs) => {
  const windowStartMs = Number(startMs) - CLEANUP_AUDIT_MARGIN_MS;
  const windowEndMs = Number(endMs) + CLEANUP_AUDIT_MARGIN_MS;
  const ids = new Set();
  if (!db || !Number.isFinite(windowStartMs) || !Number.isFinite(windowEndMs)) {
    return { ids, windowStartMs, windowEndMs, scanned: 0 };
  }
  const q = query(ref(db, 'auditLog'), orderByChild('timestamp'), startAt(windowStartMs), endAt(windowEndMs));
  const snapshot = await get(q);
  let scanned = 0;
  if (snapshot.exists()) {
    // Statement body, not a concise one: DataSnapshot.forEach stops
    // enumerating as soon as the callback returns a truthy value, and
    // Set.add() returns the set itself — which is truthy. A concise arrow
    // here would yield exactly one id no matter how many exist.
    snapshot.forEach((child) => {
      scanned += 1;
      const entry = child.val();
      if (entry?.actionType === AUDIT_ACTION_TYPES.BOOKING_SUBMITTED && entry.entityId) {
        ids.add(entry.entityId);
      }
    });
  }
  return { ids, windowStartMs, windowEndMs, scanned };
};

/**
 * The timestamp of the oldest audit entry of any kind — i.e. how far back the
 * audit log can answer for. A booking created before this cannot be judged by
 * the submit-entry test at all.
 *
 * @param {object} db
 * @returns {Promise<number|null>} epoch ms, or null when the log is empty
 */
export const fetchAuditCoverageStart = async (db) => {
  if (!db) return null;
  // orderByChild('timestamp') + limitToFirst(1): the server sorts on the
  // existing index and sends back exactly one entry. Ordering by timestamp is
  // also one of the two orderings /auditLog's read rule permits at all.
  const q = query(ref(db, 'auditLog'), orderByChild('timestamp'), limitToFirst(1));
  const snapshot = await get(q);
  let oldest = null;
  if (snapshot.exists()) {
    snapshot.forEach((child) => {
      const ts = Number(child.val()?.timestamp);
      if (Number.isFinite(ts) && (oldest === null || ts < oldest)) oldest = ts;
    });
  }
  return oldest;
};

/**
 * Bookings whose `createdAt` falls inside [startIso, endIso].
 *
 * `createdAt` is stored as an ISO 8601 UTC string (App.jsx writes
 * getPhNow().toISOString()), and ISO strings sort lexicographically in the
 * same order as the instants they name — which is what lets a string range
 * query stand in for a time range.
 *
 * NOTE: needs `createdAt` in /bookings `.indexOn`. Without it Firebase still
 * returns the right answer but sorts client-side, meaning the whole node is
 * downloaded. See database.rules.json.
 *
 * @param {object} db
 * @param {string} startIso - inclusive lower bound
 * @param {string} endIso - inclusive upper bound
 * @returns {Promise<Array<object>>} booking records, each with its Firebase id
 */
export const fetchBookingsInCreatedRange = async (db, startIso, endIso) => {
  if (!db || !startIso || !endIso) return [];
  const q = query(ref(db, 'bookings'), orderByChild('createdAt'), startAt(startIso), endAt(endIso));
  const snapshot = await get(q);
  const list = [];
  if (snapshot.exists()) {
    snapshot.forEach((child) => { list.push({ id: child.key, ...child.val() }); });
  }
  return list;
};

/**
 * Bookings carrying no usable `createdAt` at all.
 *
 * These cannot appear in any range query — Firebase orders by type and a
 * record with the child missing sorts outside every string bound — so the
 * only way to see them is to read the node and look. That is the whole
 * bookings node in one download, which is why the screen puts this behind its
 * own switch and says what it costs.
 *
 * Worth having: a record written by a script is under no obligation to
 * include the field, and one that omits it would otherwise be invisible to
 * this screen forever.
 *
 * @param {object} db
 * @returns {Promise<{records: Array<object>, scannedCount: number}>}
 */
export const fetchBookingsWithoutCreatedAt = async (db) => {
  if (!db) return { records: [], scannedCount: 0 };
  const snapshot = await get(ref(db, 'bookings'));
  const records = [];
  let scannedCount = 0;
  if (snapshot.exists()) {
    snapshot.forEach((child) => {
      scannedCount += 1;
      const value = child.val() || {};
      const createdAt = value.createdAt;
      if (typeof createdAt !== 'string' || !createdAt.trim() || Number.isNaN(Date.parse(createdAt))) {
        records.push({ id: child.key, ...value });
      }
    });
  }
  return { records, scannedCount };
};

/**
 * Splits booking records into the groups the screen renders.
 *
 * A multi-date booking is stored as one record per date sharing a `groupId`,
 * and its single audit entry is filed under that group id — so a sibling
 * date-record is clean when EITHER its own id or its groupId was submitted.
 * Missing that second check would condemn every non-primary date of every
 * genuine multi-date booking.
 *
 * @param {Array<object>} bookings - records with `id`
 * @param {Set<string>} submittedIds - from fetchSubmittedEntityIds
 * @returns {{dirty: Array<object>, abandoned: Array<object>, clean: Array<object>}}
 */
export const classifyBookings = (bookings, submittedIds) => {
  const result = { dirty: [], abandoned: [], clean: [] };
  (bookings || []).forEach((booking) => {
    const hasSubmitRecord = submittedIds.has(booking.id)
      || (booking.groupId ? submittedIds.has(booking.groupId) : false);
    if (hasSubmitRecord) {
      result.clean.push(booking);
    } else if (booking.status === 'awaiting_payment') {
      result.abandoned.push(booking);
    } else {
      result.dirty.push(booking);
    }
  });
  return result;
};

/**
 * Which group a record belongs to, for a per-row badge.
 * @param {object} booking
 * @param {Set<string>} submittedIds
 * @returns {string} one of CLEANUP_GROUPS
 */
export const bookingCleanupGroup = (booking, submittedIds) => {
  const hasSubmitRecord = submittedIds.has(booking.id)
    || (booking.groupId ? submittedIds.has(booking.groupId) : false);
  if (hasSubmitRecord) return CLEANUP_GROUPS.CLEAN;
  return booking.status === 'awaiting_payment' ? CLEANUP_GROUPS.ABANDONED : CLEANUP_GROUPS.DIRTY;
};

/**
 * Everything that gets removed alongside the booking records themselves.
 *
 * Deleting only `bookings/<id>` would leave two kinds of debris behind, both
 * of which are visible to guests:
 *
 *   - `slotLocks/<date>/<slot>/<court>` still naming the deleted booking,
 *     which keeps a court showing as unavailable with no reservation to
 *     explain it.
 *   - `equipmentHolds/<date>/<equipmentId>/<id>`, which keeps rental units
 *     reserved the same way.
 *
 * And one that is merely waste: `bookingReceipts/<id>`, an image of up to
 * ~350KB per record, paid for in storage and in the download quota.
 *
 * NOT removed, deliberately:
 *   - `/auditLog` — append-only by rule (`!data.exists()`), and by definition
 *     these records have nothing there anyway.
 *   - `/paymentReferences/<reference>` — the rules allow deleting a claim only
 *     within 5 minutes of it being made, precisely so a spent receipt cannot
 *     be freed for re-use later. A dirty record almost never holds one (the
 *     claim happens during a real checkout), and releasing a genuine one would
 *     undo de-duplication.
 *   - `/slotOverrides`, `/equipmentManualUsage` — admin-authored, keyed by
 *     date rather than by booking, so nothing in them belongs to a booking.
 *
 * @param {Array<object>} bookings - records with `id` and `date`
 * @returns {{paths: Object, byDate: Object<string, string[]>, receiptCount: number}}
 */
export const buildDeletionPlan = (bookings) => {
  const paths = {};
  const byDate = {};
  let receiptCount = 0;
  (bookings || []).forEach((booking) => {
    if (!booking?.id) return;
    paths[`bookings/${booking.id}`] = null;
    // Unconditional: `hasPaymentScreenshot` is only a flag, and a record whose
    // flag was never set can still have an image (a legacy write, or a script
    // imitating one). Deleting an absent path is a no-op in Firebase.
    paths[`bookingReceipts/${booking.id}`] = null;
    receiptCount += booking.hasPaymentScreenshot ? 1 : 0;
    const date = booking.date;
    if (date) {
      if (!byDate[date]) byDate[date] = [];
      byDate[date].push(booking.id);
    }
  });
  return { paths, byDate, receiptCount };
};

/**
 * Drops every slot lock and equipment hold belonging to the given bookings.
 *
 * One pass per DATE rather than per booking: releaseBookingSlots() reads the
 * whole `slotLocks/<date>` map for a single booking id, so calling it 4,000
 * times would re-read the same handful of dates 4,000 times. This does the
 * same work with one read and one multi-path delete per date.
 *
 * @param {object} db
 * @param {Object<string, string[]>} byDate - { 'yyyy-MM-dd': [bookingId, ...] }
 * @returns {Promise<void>}
 */
export const releaseClaimsForBookings = async (db, byDate) => {
  if (!db) return;
  const dates = Object.keys(byDate || {});
  for (const date of dates) {
    const ids = new Set(byDate[date]);

    // eslint-disable-next-line no-await-in-loop
    await removeSlotLocksWhere(db, date, (lock) => ids.has(lock.bookingId)).catch((error) => {
      // A failed release must not abort the run: the booking records are the
      // point, and a stale lock is visible and fixable in the grid, whereas a
      // half-deleted batch is neither.
      console.error(`Cleanup: could not release slot locks for ${date}`, error);
    });

    // eslint-disable-next-line no-await-in-loop
    await runTransaction(ref(db, `equipmentHolds/${date}`), (current) => {
      if (!current) return current;
      const next = {};
      Object.entries(current).forEach(([equipmentId, holds]) => {
        const remaining = {};
        Object.entries(holds || {}).forEach(([bookingId, hold]) => {
          if (ids.has(bookingId)) return;
          remaining[bookingId] = hold;
        });
        if (Object.keys(remaining).length > 0) next[equipmentId] = remaining;
      });
      return next;
    }).catch((error) => {
      console.error(`Cleanup: could not release equipment holds for ${date}`, error);
    });
  }
};

/**
 * Deletes the given bookings and everything listed in buildDeletionPlan.
 *
 * Claims come off FIRST. If the run dies halfway, a booking whose claims were
 * released but whose record survives is merely a reservation the grid shows as
 * free — recoverable, and visible. The reverse order would leave locks with no
 * record to trace them back to, which nothing in the app can clear.
 *
 * Writes go out in chunks because one multi-path update carrying 8,000 paths
 * is a single request Firebase may reject outright, and a rejection there
 * would mean nothing at all had been deleted.
 *
 * @param {object} db
 * @param {Array<object>} bookings - the exact records to delete
 * @param {(progress: {done: number, total: number}) => void} [onProgress]
 * @returns {Promise<{deleted: number, failedChunks: number, receiptsRemoved: number}>}
 */
export const deleteDirtyBookings = async (db, bookings, onProgress = null) => {
  const list = (bookings || []).filter(b => b?.id);
  if (!db || list.length === 0) return { deleted: 0, failedChunks: 0, receiptsRemoved: 0 };

  const { byDate, receiptCount } = buildDeletionPlan(list);
  await releaseClaimsForBookings(db, byDate);

  let deleted = 0;
  let failedChunks = 0;
  for (let i = 0; i < list.length; i += CLEANUP_DELETE_CHUNK_SIZE) {
    const chunk = list.slice(i, i + CLEANUP_DELETE_CHUNK_SIZE);
    const { paths } = buildDeletionPlan(chunk);
    try {
      // eslint-disable-next-line no-await-in-loop
      await update(ref(db), paths);
      deleted += chunk.length;
    } catch (error) {
      failedChunks += 1;
      console.error('Cleanup: a delete batch failed', error);
    }
    if (onProgress) onProgress({ done: Math.min(i + chunk.length, list.length), total: list.length });
  }

  return { deleted, failedChunks, receiptsRemoved: receiptCount };
};
